import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import EmptyState from "@/components/EmptyState";
import { useExperiments } from "@/hooks/useExperiments";

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(var(--destructive))'];

const ParticipantDistributionChart: React.FC = () => {
  const { data: experiments } = useExperiments();
  
  // Get all experiment IDs
  const experimentIds = experiments?.map(exp => exp.id) || [];

  const { data, isLoading } = useQuery({
    queryKey: ["participant-distribution", experimentIds],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('metabase-query/85', {
        body: {
          parameters: [
            {
              type: 'text',
              target: ['variable', ['template-tag', 'experiment_ids']],
              value: experimentIds.join(','),
            },
          ],
          format_rows: true,
          pivot_results: false,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch participant distribution');
      }

      return Array.isArray(data) ? data : [];
    },
    enabled: experimentIds.length > 0,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Participant Distribution</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  // Process data from metabase query 85
  const distributionData = data || [];

  // If no experiments or participants, show empty state
  if (distributionData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Participant Distribution</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <EmptyState 
            title="No participants yet"
            description="Start inviting participants to your experiments to see the distribution here"
          />
        </CardContent>
      </Card>
    );
  }

  // Prepare data for pie chart using the new format (name, unique_participants)
  const chartData = distributionData.map((exp: any, index: number) => ({
    name: exp.name || 'Unknown',
    value: parseInt(exp.unique_participants) || 0,
    color: COLORS[index % COLORS.length]
  }));

  const totalParticipants = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Participant Distribution</CardTitle>
        <p className="text-sm text-muted-foreground">
          {totalParticipants} participants across {distributionData.length} experiments
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => [value, 'Participants']}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '20px',
                  fontSize: '12px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default ParticipantDistributionChart;